import { Box, Button, Chip, TextField } from '@mui/material';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { tokens } from '@synergy/theme-palo';
import { useSearchParams } from 'react-router-dom';

import { SearchCuration, SearchPreview, SearchPreviewDraft, SearchPreviewHit } from '../models/search-quality';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

/**
 * Ansicht 4 — das Probierfeld. One term, two answers side by side: how the shop
 * answers it today, and how it would answer it with the draft applied — another
 * term, other pins and exclusions, other weights. Nothing here goes live until
 * the person saves the pins as curation; weights and replacements never do.
 *
 * The draft replaces the stored rules rather than adding to them, so it is
 * seeded from them: what stands in the mask is exactly what the draft runs with.
 */
/**
 * Worked examples for the weight fields. Values a person can reason about:
 * 0 switches a rule off, 15 is the live strength of the name-start rule, 40 is
 * deliberately too much — it shows what a dominant weight does to the list.
 */
const PRESETS: { id: string; values: Partial<SearchPreviewDraft> }[] = [
    { id: 'live', values: {} },
    { id: 'nameStrong', values: { nameStartBoost: 40 } },
    { id: 'nameOff', values: { nameStartBoost: 0 } },
    { id: 'allWords', values: { allWordsBoost: 15 } },
    { id: 'strict', values: { minScore: 8 } },
    { id: 'everyWord', values: { minimumShouldMatch: '100%' } },
    { id: 'anyWord', values: { minimumShouldMatch: '1' } },
    { id: 'loose', values: { minScore: 0.5 } },
];

export const SearchPreviewView = () => {
    const dispatch = useThunkDispatch();
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const [term, setTerm] = useState(searchParams.get('term') ?? '');
    const [loadedTerm, setLoadedTerm] = useState<string | null>(null);
    const [draft, setDraft] = useState<SearchPreviewDraft>({ pinnedIds: [], hiddenIds: [] });
    const [preview, setPreview] = useState<SearchPreview | null>(null);
    const [stored, setStored] = useState<SearchCuration | null>(null);
    const [labels, setLabels] = useState<Record<number, string>>({});
    const [requesting, setRequesting] = useState(false);
    const [failed, setFailed] = useState(false);

    const run = useCallback(
        (value: string, nextDraft: SearchPreviewDraft) => {
            setRequesting(true);
            setFailed(false);

            void dispatch(async (_dispatch, _getState, { api }) => {
                const result = await api.searchQuality.preview(value, nextDraft);
                if (result.success) {
                    setPreview(result.result);
                    setLabels(known => {
                        const next = { ...known };
                        [...result.result.live.hits, ...result.result.draft.hits].forEach(hit => {
                            next[hit.productId] = hit.name;
                        });
                        return next;
                    });
                }
                setFailed(!result.success);
                setRequesting(false);
            });
        },
        [dispatch]
    );

    // A fresh term starts from the rules people saved: the draft side then shows
    // the search as it is, and every change in the mask is a change against that.
    const start = useCallback(
        (value: string) => {
            const trimmed = value.trim();
            if ('' === trimmed) {
                return;
            }

            void dispatch(async (_dispatch, _getState, { api }) => {
                const rules = await api.searchQuality.getCuration(trimmed);
                const current = rules.success ? rules.result : null;
                setStored(current);
                setLabels(known => {
                    const next = { ...known };
                    current?.rules.forEach(rule => {
                        next[rule.productId] = rule.name;
                    });
                    return next;
                });

                const seeded: SearchPreviewDraft = {
                    pinnedIds:
                        current?.rules
                            .filter(rule => 'pin' === rule.rule && rule.active)
                            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                            .map(rule => rule.productId) ?? [],
                    hiddenIds:
                        current?.rules
                            .filter(rule => 'hide' === rule.rule && rule.active)
                            .map(rule => rule.productId) ?? [],
                };
                setDraft(seeded);
                setLoadedTerm(trimmed);
                run(trimmed, seeded);
            });
        },
        [dispatch, run]
    );

    useEffect(() => {
        const fromUrl = searchParams.get('term');
        if (fromUrl) {
            start(fromUrl);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const trimmed = term.trim();
        if ('' === trimmed) {
            return;
        }
        if (trimmed === loadedTerm) {
            run(trimmed, draft);
            return;
        }
        const next = new URLSearchParams(searchParams);
        next.set('term', trimmed);
        setSearchParams(next);
    };

    const change = (partial: Partial<SearchPreviewDraft>, rerun = false) => {
        const next = { ...draft, ...partial };
        setDraft(next);
        if (rerun && loadedTerm) {
            run(loadedTerm, next);
        }
    };

    const applyPreset = (values: Partial<SearchPreviewDraft>) => {
        const next = {
            ...draft,
            nameStartBoost: undefined,
            allWordsBoost: undefined,
            minScore: undefined,
            minimumShouldMatch: undefined,
            ...values,
        };
        setDraft(next);
        if (loadedTerm) {
            run(loadedTerm, next);
        }
    };

    const pin = (productId: number) =>
        change(
            {
                pinnedIds: [...draft.pinnedIds.filter(id => id !== productId), productId],
                hiddenIds: draft.hiddenIds.filter(id => id !== productId),
            },
            true
        );

    const hide = (productId: number) =>
        change(
            {
                pinnedIds: draft.pinnedIds.filter(id => id !== productId),
                hiddenIds: [...draft.hiddenIds.filter(id => id !== productId), productId],
            },
            true
        );

    const release = (productId: number) =>
        change(
            {
                pinnedIds: draft.pinnedIds.filter(id => id !== productId),
                hiddenIds: draft.hiddenIds.filter(id => id !== productId),
            },
            true
        );

    // Saving means: make the stored rules equal to the draft's pins and hides.
    // Weights and a replaced term stay out on purpose — they have their own path.
    const apply = () => {
        if (!loadedTerm) {
            return;
        }
        void dispatch(async (_dispatch, _getState, { api }) => {
            const keep = new Set([...draft.pinnedIds, ...draft.hiddenIds]);
            for (const rule of stored?.rules ?? []) {
                if (!keep.has(rule.productId)) {
                    await api.searchQuality.removeCuration(loadedTerm, rule.productId);
                }
            }
            for (const [index, productId] of draft.pinnedIds.entries()) {
                await api.searchQuality.saveCuration(loadedTerm, productId, 'pin', { position: index + 1 });
            }
            for (const productId of draft.hiddenIds) {
                await api.searchQuality.saveCuration(loadedTerm, productId, 'hide');
            }
            start(loadedTerm);
        });
    };

    const storedPins =
        stored?.rules
            .filter(rule => 'pin' === rule.rule && rule.active)
            .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            .map(rule => rule.productId) ?? [];
    const storedHides =
        stored?.rules.filter(rule => 'hide' === rule.rule && rule.active).map(rule => rule.productId) ?? [];
    const curationDiffers =
        JSON.stringify(storedPins) !== JSON.stringify(draft.pinnedIds) ||
        JSON.stringify([...storedHides].sort()) !== JSON.stringify([...draft.hiddenIds].sort());

    const livePosition = new Map((preview?.live.hits ?? []).map((hit, index) => [hit.productId, index]));
    const draftIds = new Set((preview?.draft.hits ?? []).map(hit => hit.productId));

    const movement = (hit: SearchPreviewHit, index: number) => {
        const before = livePosition.get(hit.productId);
        if (undefined === before) {
            return (
                <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={<FormattedMessage id="app.searchConsole.preview.new" />}
                />
            );
        }
        if (before === index) {
            return null;
        }
        return (
            <Box
                component="span"
                sx={{
                    fontFamily: tokens.font.mono,
                    fontSize: '11px',
                    color: before > index ? tokens.color.ok : tokens.color.crit,
                }}
            >
                {before > index ? `▲${before - index}` : `▼${index - before}`}
            </Box>
        );
    };

    const numberField = (
        labelId: string,
        value: number | undefined,
        fallback: number | undefined,
        onChange: (next?: number) => void
    ) => (
        <TextField
            size="small"
            type="number"
            label={intl.formatMessage({ id: labelId })}
            value={value ?? ''}
            placeholder={undefined === fallback ? undefined : String(fallback)}
            onChange={event => onChange('' === event.target.value ? undefined : Number(event.target.value))}
            sx={{ width: 150, backgroundColor: tokens.color.surface }}
            InputLabelProps={{ shrink: true }}
        />
    );

    const hitRow = (hit: SearchPreviewHit, index: number, side: 'live' | 'draft') => {
        const pinned = draft.pinnedIds.includes(hit.productId);
        return (
            <Box
                key={hit.productId}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '26px 1fr auto',
                    gap: '8px',
                    alignItems: 'center',
                    padding: '7px 10px',
                    backgroundColor: tokens.color.surface,
                    border: `1px solid ${tokens.color.line}`,
                    borderRadius: '8px',
                    opacity: 'live' === side && !draftIds.has(hit.productId) ? 0.55 : 1,
                }}
            >
                <Box
                    sx={{
                        fontFamily: tokens.font.mono,
                        fontSize: '12px',
                        color: tokens.color.ink3,
                        textAlign: 'right',
                    }}
                >
                    {index + 1}
                </Box>
                <Box>
                    <Box
                        sx={{
                            fontSize: '13px',
                            color: tokens.color.ink,
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                        }}
                    >
                        {hit.name}
                        {'draft' === side && movement(hit, index)}
                        {'draft' === side && pinned && (
                            <Chip
                                size="small"
                                color="primary"
                                label={
                                    <FormattedMessage
                                        id="app.searchConsole.curation.pinned"
                                        values={{ position: draft.pinnedIds.indexOf(hit.productId) + 1 }}
                                    />
                                }
                            />
                        )}
                        {'live' === side && !draftIds.has(hit.productId) && (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={<FormattedMessage id="app.searchConsole.preview.gone" />}
                            />
                        )}
                    </Box>
                    <Box sx={{ fontFamily: tokens.font.mono, fontSize: '11px', color: tokens.color.accent }}>
                        {hit.number}
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: '2px' }}>
                    {pinned ? (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => release(hit.productId)}
                            sx={{ fontSize: '11px' }}
                        >
                            <FormattedMessage id="app.searchConsole.curation.unpin" />
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => pin(hit.productId)}
                            sx={{ fontSize: '11px' }}
                        >
                            <FormattedMessage id="app.searchConsole.curation.pin" />
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={() => hide(hit.productId)}
                        sx={{ fontSize: '11px', color: tokens.color.ink3 }}
                    >
                        <FormattedMessage id="app.searchConsole.curation.hide" />
                    </Button>
                </Box>
            </Box>
        );
    };

    const column = (side: 'live' | 'draft') => {
        if (!preview) {
            return null;
        }
        const list = preview[side];
        const isDraft = 'draft' === side;

        return (
            <Box
                sx={{
                    border: `1px solid ${isDraft ? tokens.color.accentLine : tokens.color.line}`,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    backgroundColor: tokens.color.surface,
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: '10px',
                        padding: '9px 14px',
                        backgroundColor: isDraft ? tokens.color.accentSoft : tokens.color.surfaceAlt,
                        borderBottom: `1px solid ${isDraft ? tokens.color.accentLine : tokens.color.line}`,
                    }}
                >
                    <Box
                        sx={{
                            fontSize: '12px',
                            fontWeight: 640,
                            color: isDraft ? tokens.color.accentHi : tokens.color.ink2,
                        }}
                    >
                        <FormattedMessage id={`app.searchConsole.preview.${side}Title`} />
                    </Box>
                    <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                        <FormattedMessage
                            id="app.searchConsole.preview.total"
                            values={{
                                total: list.total.toLocaleString('de-AT'),
                                term: isDraft ? preview.draftTerm : preview.term,
                            }}
                        />
                    </Box>
                    {isDraft && curationDiffers && (
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: 'auto' }}>
                            <Chip
                                size="small"
                                color="warning"
                                label={<FormattedMessage id="app.searchConsole.preview.unsaved" />}
                            />
                            <Button size="small" variant="contained" onClick={apply} disabled={requesting}>
                                <FormattedMessage id="app.searchConsole.preview.applyShort" />
                            </Button>
                        </Box>
                    )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px' }}>
                    {isDraft && draft.hiddenIds.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '6px',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                fontSize: '12px',
                            }}
                        >
                            <Box sx={{ color: tokens.color.ink3 }}>
                                <FormattedMessage id="app.searchConsole.preview.hiddenTitle" />
                            </Box>
                            {draft.hiddenIds.map(productId => (
                                <Chip
                                    key={productId}
                                    size="small"
                                    label={labels[productId] ?? `#${productId}`}
                                    onDelete={() => release(productId)}
                                />
                            ))}
                        </Box>
                    )}
                    {list.hits.length === 0 && (
                        <Box sx={{ fontSize: '12px', color: tokens.color.ink2, padding: '2px 4px' }}>
                            <FormattedMessage id="app.searchConsole.preview.empty" />
                        </Box>
                    )}
                    {list.hits.map((hit, index) => hitRow(hit, index, side))}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <Box component="form" onSubmit={submit} sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <TextField
                    size="small"
                    value={term}
                    onChange={event => setTerm(event.target.value)}
                    placeholder={intl.formatMessage({ id: 'app.searchConsole.preview.placeholder' })}
                    sx={{ width: 300 }}
                />
                <Button type="submit" variant="contained" disabled={requesting || '' === term.trim()}>
                    <FormattedMessage id="app.searchConsole.preview.run" />
                </Button>
            </Box>

            {failed && (
                <Box sx={{ fontSize: '12px', color: tokens.color.crit }}>
                    <FormattedMessage id="app.searchConsole.preview.failed" />
                </Box>
            )}

            {!failed && preview === null && !requesting && (
                <Box sx={{ fontSize: '12px', color: tokens.color.ink2 }}>
                    <FormattedMessage id="app.searchConsole.preview.intro" />
                </Box>
            )}

            {preview && (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                            padding: '12px 14px',
                            border: `1px solid ${tokens.color.line}`,
                            borderRadius: '10px',
                            backgroundColor: tokens.color.surfaceAlt,
                        }}
                    >
                        <Box sx={{ fontSize: '12px', fontWeight: 640, color: tokens.color.ink }}>
                            <FormattedMessage id="app.searchConsole.preview.draftPanelTitle" />
                        </Box>
                        <Box sx={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                                size="small"
                                value={draft.draftTerm ?? ''}
                                onChange={event =>
                                    change({ draftTerm: '' === event.target.value ? undefined : event.target.value })
                                }
                                label={intl.formatMessage({ id: 'app.searchConsole.preview.draftTerm' })}
                                sx={{ width: 240, backgroundColor: tokens.color.surface }}
                                InputLabelProps={{ shrink: true }}
                            />
                            {numberField(
                                'app.searchConsole.preview.nameStartBoost',
                                draft.nameStartBoost,
                                preview.defaults.nameStartBoost,
                                next => change({ nameStartBoost: next })
                            )}
                            {numberField(
                                'app.searchConsole.preview.allWordsBoost',
                                draft.allWordsBoost,
                                preview.defaults.allWordsBoost,
                                next => change({ allWordsBoost: next })
                            )}
                            {numberField(
                                'app.searchConsole.preview.minScore',
                                draft.minScore,
                                preview.defaults.minScore,
                                next => change({ minScore: next })
                            )}
                            <TextField
                                size="small"
                                value={draft.minimumShouldMatch ?? ''}
                                onChange={event =>
                                    change({
                                        minimumShouldMatch: '' === event.target.value ? undefined : event.target.value,
                                    })
                                }
                                label={intl.formatMessage({ id: 'app.searchConsole.preview.coverage' })}
                                placeholder={preview.defaults.minimumShouldMatch}
                                sx={{ width: 150, backgroundColor: tokens.color.surface }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                                <FormattedMessage id="app.searchConsole.preview.presets" />
                            </Box>
                            {PRESETS.map(preset => (
                                <Chip
                                    key={preset.id}
                                    size="small"
                                    variant="outlined"
                                    clickable={true}
                                    onClick={() => applyPreset(preset.values)}
                                    label={<FormattedMessage id={`app.searchConsole.preview.preset.${preset.id}`} />}
                                    title={intl.formatMessage({
                                        id: `app.searchConsole.preview.preset.${preset.id}.hint`,
                                    })}
                                    sx={{ backgroundColor: tokens.color.surface }}
                                />
                            ))}
                        </Box>
                        <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                            <FormattedMessage id="app.searchConsole.preview.controlsHint" />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', alignItems: 'start' }}>
                        {column('live')}
                        {column('draft')}
                    </Box>

                    <Box sx={{ fontSize: '11px', color: tokens.color.ink3 }}>
                        <FormattedMessage id="app.searchConsole.preview.applyHint" />
                    </Box>
                </>
            )}
        </Box>
    );
};
